// Finished-size picker.
//
// Behavior driven entirely by enum-option `meta`:
//   - Filter to options whose `applies_to` contains the current
//     product_family (so label sheets never show on book quotes,
//     and book sizes never show on label quotes).
//   - Drop a retired size (`meta.deprecated`) unless it is the value already
//     on the line, so an older quote still shows and keeps its pick.
//   - Top tier ("common") renders as chips up front, popularity-sorted.
//     Per m2m_data/CONFIGURATION_SURFACE.md frequency tables.
//   - Less-common sizes collapsed behind "Show more sizes" — disclosure
//     pattern keeps the default surface tight per Nielsen / Vistaprint /
//     Moo configurators. Options carrying `meta.group` (label sheets, by
//     shape) render there under one heading per group, in first-seen order.
//   - A family whose picker_config sets `control: 'select'` (label sheets)
//     gets one dropdown holding every size instead, with an <optgroup> per
//     `meta.group`, beside the same custom-size input.
//   - The custom-size escape is an ALWAYS-VISIBLE text input occupying the last
//     cell of the size grid, inline with the chips in chips mode, beside the
//     dropdown in select mode (not a chip you click to
//     reveal, not a row below). Typing dimensions selects the custom size. It
//     shares the chips' cell height so the grid reads as one consistent set.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  FormFieldDto,
  FormFieldEnumOptionDto,
  ProductFamilyDto,
  SizeGridFamilyConfig,
} from 'shared';

import { formatCustomSize } from './formatCustomSize.js';

type Props = {
  field: FormFieldDto;
  value: unknown;
  // The current `<field.key>_custom` companion value — the free-text dimensions
  // the customer types when "Custom size" is selected. It lives as a sibling key
  // in the line config (not a schema field); SpecSummary reads it back.
  customValue: string | undefined;
  // Atomic multi-key config setter (the same one SchemaForm hands FamilyCardGrid).
  // Selecting a real size sets the size AND clears any stale custom dimensions in
  // ONE patch — two sequential single-key sets would clobber each other off the
  // same stale snapshot.
  setValues: (patch: Record<string, unknown>) => void;
  // Internal family code of the current line. Drives filtering +
  // picker_config lookup.
  family: string | undefined;
  // The active schema's product_families list. The picker pulls this
  // family's picker_config.finished_size off the matching row so policy
  // (disclosure threshold, default-expanded, sort) is declarative.
  product_families: ProductFamilyDto[];
  // Scroll-target id + error wiring, placed on the picker's root container so
  // the missing-field summary can jump here (the chips are buttons, not the
  // target). See fieldIdPrefix / FieldRow in SchemaForm.
  id?: string;
  // The already-resolved aria-describedby for this field (the inline error
  // message when flagged, plus any host-supplied description). Built in
  // SchemaForm's FieldRow, never joined here.
  describedBy?: string;
  // Whether the field is flagged. Only the dropdown can carry aria-invalid; the
  // chip picker's root is a plain div.
  invalid?: boolean;
};

type SizeMeta = {
  applies_to?: string[];
  popularity?: number;
  common?: boolean;
  is_other?: boolean;
  deprecated?: boolean;
  sublabel?: string;
  group?: string;
};

function readMeta(opt: FormFieldEnumOptionDto): SizeMeta {
  return (opt.meta ?? {}) as SizeMeta;
}

// Split sizes by `meta.group`, keeping sort order within each group. Ungrouped
// sizes (every size of a family without groups, or a retired size an older
// quote still carries) lead as one unheaded run, so they never read as part of
// the last heading.
function groupByMeta(
  options: FormFieldEnumOptionDto[],
): { group: string | undefined; options: FormFieldEnumOptionDto[] }[] {
  const groups: { group: string | undefined; options: FormFieldEnumOptionDto[] }[] = [];
  for (const opt of options) {
    const group = readMeta(opt).group;
    const existing = groups.find((g) => g.group === group);
    if (existing) existing.options.push(opt);
    else groups.push({ group, options: [opt] });
  }
  return groups.sort((a, b) => Number(a.group !== undefined) - Number(b.group !== undefined));
}

function readFamilyConfig(
  families: ProductFamilyDto[],
  familyCode: string | undefined
): SizeGridFamilyConfig {
  if (!familyCode) return {};
  const fam = families.find((f) => f.code === familyCode);
  const cfg = (fam?.picker_config ?? null) as Record<string, unknown> | null;
  const sizeCfg = (cfg?.['finished_size'] ?? null) as SizeGridFamilyConfig | null;
  return sizeCfg ?? {};
}

export function SizeGrid({ field, value, customValue, setValues, family, product_families, id, describedBy, invalid }: Props): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);

  // Companion config key holding the free-text dimensions for the custom size.
  const customKey = `${field.key}_custom`;

  // Pull the per-family policy. Missing config = defaults.
  const cfg = readFamilyConfig(product_families, family);
  const disclosure_threshold = cfg.disclosure_threshold ?? 2;
  const initial_expanded = cfg.initial_expanded ?? false;
  const sort_mode = cfg.sort ?? 'popularity';
  const control = cfg.control ?? 'chips';

  // Single source of truth for the disclosure widget. Initial state
  // honors the family's `initial_expanded` policy.
  const [expanded, setExpanded] = useState<boolean>(initial_expanded);

  const familyApplicable = (m: SizeMeta): boolean => {
    if (!m.applies_to || m.applies_to.length === 0) return true;
    if (!family) return true;
    return m.applies_to.includes(family);
  };

  // Filter to options relevant to the current family, minus retired sizes the
  // line does not already carry.
  const relevant = field.enum_options.filter((opt) => {
    const m = readMeta(opt);
    return familyApplicable(m) && (m.deprecated !== true || opt.value === value);
  });

  // Sort policy comes from family picker_config; default is popularity
  // (asc). "label" sorts alphabetically by display label — useful for
  // a family whose options have no meaningful popularity ranking.
  const sortFn =
    sort_mode === 'label'
      ? (a: FormFieldEnumOptionDto, b: FormFieldEnumOptionDto) => a.label.localeCompare(b.label)
      : (a: FormFieldEnumOptionDto, b: FormFieldEnumOptionDto) =>
          (readMeta(a).popularity ?? 500) - (readMeta(b).popularity ?? 500);

  const other = relevant.find((o) => readMeta(o).is_other === true);
  const sized = relevant.filter((o) => readMeta(o).is_other !== true).sort(sortFn);

  const common = sized.filter((o) => readMeta(o).common !== false);
  const less = sized.filter((o) => readMeta(o).common === false);

  const lessGroups = groupByMeta(less);

  // The enum value that marks the custom escape (e.g. 'custom'). Falls back to
  // the literal in the unlikely event a family ships no is_other option.
  const customOptionValue = other?.value ?? 'custom';

  // Selecting a real catalog size clears any stale custom dimensions in the same
  // atomic patch (two sequential single-key sets would clobber each other off
  // the same stale snapshot — see the setValues note on Props).
  const selectSize = (v: string): void => setValues({ [field.key]: v, [customKey]: undefined });

  // The custom-size input is ALWAYS visible — there is no chip to click first.
  // Typing dimensions makes "custom" the selected size; clearing the field
  // deselects custom (so a required size field stays honest) while leaving any
  // standard chip choice untouched. One atomic patch keeps the size value and
  // its companion dimensions in lockstep.
  const onCustomInput = (text: string): void => {
    if (text !== '') setValues({ [field.key]: customOptionValue, [customKey]: text });
    else if (value === customOptionValue) setValues({ [field.key]: undefined, [customKey]: undefined });
    else setValues({ [customKey]: undefined });
  };

  // On blur, tidy the typed dimensions into the catalog glyph style ("8x9" →
  // "8″ × 9″") so the custom escape reads back like every other size chip.
  // Kept off the live keystroke path (onCustomInput) so the inch marks / × don't
  // fight the customer mid-type; formatCustomSize is a no-op on non-dimension
  // text and idempotent on an already-formatted value.
  const onCustomBlur = (): void => {
    if (customValue == null || customValue.trim() === '') return;
    const formatted = formatCustomSize(customValue);
    if (formatted !== customValue) setValues({ [field.key]: customOptionValue, [customKey]: formatted });
  };

  // Items that go behind the disclosure if it renders: ONLY the less-common
  // real sizes. The custom-size input is NOT counted — it sits in its own
  // always-visible row below the grid, never hidden. The disclosure renders
  // only when enough less-common sizes meet the family's threshold; below
  // threshold they get promoted into the inline common grid instead.
  const hiddenIfDisclosed = less.length;
  const useDisclosure = hiddenIfDisclosed >= disclosure_threshold;

  // Auto-expand when the saved value is a less-common real size that lives
  // behind the disclosure, so the user's selection stays visible without a
  // manual click. (The custom input is always visible, so it needs no
  // auto-expand.)
  const selectionIsLess = less.some((o) => o.value === value);
  useEffect(() => {
    if (selectionIsLess) setExpanded(true);
  }, [selectionIsLess]);

  function chip(opt: FormFieldEnumOptionDto): JSX.Element {
    const m = readMeta(opt);
    const selected = value === opt.value;
    return (
      <button
        key={opt.value}
        type="button"
        role="radio"
        aria-checked={selected}
        className={`size-chip${selected ? ' size-chip--selected' : ''}`}
        onClick={() => selectSize(opt.value)}
      >
        <div className="size-chip__label">{opt.label}</div>
        {m.sublabel && <div className="size-chip__sublabel">{m.sublabel}</div>}
      </button>
    );
  }

  // "Custom" is the active size when the field value matches the custom escape —
  // drives the selected-state highlight on the always-visible input below.
  const customSelected = other != null && value === customOptionValue;

  const customInput = other && (
    <input
      type="text"
      className={`size-chip-input${customSelected ? ' size-chip-input--active' : ''}`}
      placeholder={t('size.customPlaceholder')}
      aria-label={t('size.customAriaLabel')}
      title={t('size.customEnterHint')}
      value={customValue ?? ''}
      onChange={(e) => onCustomInput(e.target.value)}
      onBlur={onCustomBlur}
    />
  );

  if (control === 'select') {
    // A native option is one line, so the sublabel rides after the label. The
    // custom escape is not an option here: the input beside the dropdown is it,
    // and while it holds the pick the dropdown shows its placeholder.
    const optionText = (opt: FormFieldEnumOptionDto): string => {
      const sublabel = readMeta(opt).sublabel;
      return sublabel ? `${opt.label} — ${sublabel}` : opt.label;
    };
    const selectedValue = sized.some((o) => o.value === value) ? (value as string) : '';
    return (
      <div className="size-picker size-picker--select">
        {/* The scroll-target id sits on the select itself, so the field's
            <label htmlFor> names it and the missing-field jump can focus it. */}
        <select
          id={id}
          className="size-picker__select"
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          value={selectedValue}
          onChange={(e) => {
            if (e.target.value !== '') selectSize(e.target.value);
          }}
        >
          <option value="" disabled>
            {t('size.selectPlaceholder')}
          </option>
          {groupByMeta(sized).map(({ group, options }) => {
            const items = options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {optionText(opt)}
              </option>
            ));
            return group ? (
              <optgroup key={group} label={t(`size.group.${group}`, { defaultValue: group })}>
                {items}
              </optgroup>
            ) : (
              items
            );
          })}
        </select>
        {customInput}
      </div>
    );
  }

  return (
    <div
      id={id}
      className="size-picker"
      // The picker root is a plain div (no ARIA role that supports
      // aria-invalid), so it carries only the scroll-target id + the
      // description link (aria-describedby is a global attribute, valid anywhere).
      aria-describedby={describedBy}
    >
      <div className="size-picker__grid">
        {/* display:contents radiogroup — the chips become direct grid items, but
            the custom-size textbox stays OUT of the radiogroup (a textbox is not
            a radio) while still rendering inline as the last grid cell. */}
        <div className="size-picker__radios" role="radiogroup" aria-label={field.label}>
          {common.map(chip)}
          {!useDisclosure && less.map(chip)}
        </div>
        {/* Custom size — an always-visible input sitting INLINE as the last cell
            of the size grid (not a chip you click to reveal, not a row below).
            Typing dimensions selects the custom size; it shares the chips' cell
            height. aria-label/title carry the "custom size, inches" meaning the
            compact placeholder can't fully spell out. */}
        {customInput}
      </div>

      {useDisclosure && (
        <div className="size-picker__more">
          <button
            type="button"
            className="size-picker__more-toggle"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded
              ? t('size.hideMore', { count: hiddenIfDisclosed })
              : t('size.showMore', { count: hiddenIfDisclosed })}
          </button>
          {expanded &&
            lessGroups.map(({ group, options }) => {
              const heading = group ? t(`size.group.${group}`, { defaultValue: group }) : undefined;
              return (
                <div key={group ?? 'ungrouped'} className="size-picker__group">
                  {heading && <div className="size-picker__more-label">{heading}</div>}
                  <div
                    className="size-picker__grid"
                    role="radiogroup"
                    aria-label={
                      heading
                        ? `${field.label}: ${heading}`
                        : t('size.moreSizesAria', { label: field.label })
                    }
                  >
                    {options.map(chip)}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
